import { OutlineButton, PrimaryButton } from "@components/Buttons";
import ToastConstant from "@configs/react-toastify/snackbar";
import { TOAST, NOTICE_CUSTOM_VALUE, CHARACTER_LENGTH } from "@constants/index";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { useMutationData } from "../../hooks/useMutationData";
import CustomInput from "../Forms/CustomInput";
import CustomTextarea from "../Forms/CustomTextarea";
import CustomTwoColumnSelect from "./CustomTwoColumnSelect";
import { UnsavedChangesModal } from "./index";
import useEscapeKey from "@hooks/useEscapeKey";

const NoticeInformationEditModal = ({
  isOpen,
  onClose,
  onSave,
  onNavigate,
  onUnsavedChanges,
  component,
  currentIndex,
  totalCount,
  "data-testid": dataTestId = "notice-information-edit-modal",
}) => {
  const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
  const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
  const { t } = useTranslation();
  const { colors } = useTheme();

  // Form state
  const [formData, setFormData] = useState({
    noticeInfoComponentSeq: null,
    noticeInfoSeq: null,
    url: "",
    copyright: "",
    licenseText: "",
    urlSelection: null,
    copyrightSelection: null,
    licenseTextSelection: null,
  });

  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  
  // Position navigation
  const [targetPosition, setTargetPosition] = useState(currentIndex + 1);

  // API mutation for updating notice info component
  const updateNoticeInfoComponent = useMutationData(
    () => "/notice_info_component",
    { method: "PUT" },
    {
      onSuccess: (response) => {
        ToastConstant(TOAST.success, t("project.notice-infor.save-success", "Save successfully"));
        setIsSaving(false);
        setIsSaved(true);
        setHasUnsavedChanges(false);
      },
      onError: (error) => {
        console.error("Error updating notice info component:", error);
        ToastConstant(TOAST.error, t("project.notice-infor.save-failed", "Save failed"));
        setIsSaving(false);
      }
    }
  );

  // Initialize form data when component changes
  useEffect(() => {
    if (component) {
      setFormData({
        noticeInfoComponentSeq: component.noticeInfoComponentSeq || null,
        noticeInfoSeq: component.noticeInfoSeq || null,
        url: component.url || "",
        copyright: component.copyright || "",
        licenseText: component.licenseText || "",
        urlSelection: component.urlSelection || null,
        copyrightSelection: component.copyrightSelection || null,
        licenseTextSelection: component.licenseTextSelection || null,
      });
      setHasUnsavedChanges(false);
      setIsSaved(false);
    }
  }, [component]);

  // Update target position when currentIndex changes
  useEffect(() => {
    setTargetPosition(currentIndex + 1);
  }, [currentIndex]);

  // Track changes and notify parent
  useEffect(() => {
    onUnsavedChanges(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChanges]);

  // Handle field changes
  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasUnsavedChanges(true);
    setIsSaved(false);
  };

  // Handle selection changes for dropdowns
  const handleSelectionChange = (field, selection) => {
    const updatedFormData = { ...formData };
    updatedFormData[field] = selection;

    // If selection is "Custom", clear the corresponding text field
    if (selection && selection.name === NOTICE_CUSTOM_VALUE) {
      if (field === "urlSelection") {
        updatedFormData.url = "";
      } else if (field === "copyrightSelection") {
        updatedFormData.copyright = "";
      } else if (field === "licenseTextSelection") {
        updatedFormData.licenseText = "";
      }
    } else if (selection) {
      // If not "Custom", populate the text field with the selection value
      if (field === "urlSelection") {
        updatedFormData.url = selection.name;
      } else if (field === "copyrightSelection") {
        updatedFormData.copyright = selection.name;
      } else if (field === "licenseTextSelection") {
        updatedFormData.licenseText = selection.name;
      }
    }

    setFormData(updatedFormData);
    setHasUnsavedChanges(true);
    setIsSaved(false);
  };

  const sortOptionsWithCustomFirst = (options) => {
    if (!options || !Array.isArray(options)) return [];
    
    return [...options].sort((a, b) => {
      const aIsCustom = a.name === NOTICE_CUSTOM_VALUE || a.displayName === NOTICE_CUSTOM_VALUE;
      const bIsCustom = b.name === NOTICE_CUSTOM_VALUE || b.displayName === NOTICE_CUSTOM_VALUE;
      
      if (aIsCustom && !bIsCustom) return -1;
      if (!aIsCustom && bIsCustom) return 1;
      return 0;
    });
  };

  // Helper function to check if a field should be disabled
  const isFieldDisabled = (field) => {
    if (field === "url") {
      return !formData.urlSelection || (formData.urlSelection && formData.urlSelection.name !== NOTICE_CUSTOM_VALUE);
    } else if (field === "copyright") {
      return (
        !formData.copyrightSelection || (formData.copyrightSelection && formData.copyrightSelection.name !== NOTICE_CUSTOM_VALUE)
      );
    } else if (field === "licenseText") {
      return (
        !formData.licenseTextSelection ||
        (formData.licenseTextSelection && formData.licenseTextSelection.name !== NOTICE_CUSTOM_VALUE)
      );
    }
    return false;
  };

  // Handle save
  const handleSave = async () => {
    if (isSaving || isSaved) return;

    setIsSaving(true);
    try {
      // Prepare the request body for the API
      const requestBody = {
        noticeInfoComponentSeq: formData.noticeInfoComponentSeq,
        noticeInfoSeq: formData.noticeInfoSeq,
        componentName: component.componentName || "",
        componentVersion: component.componentVersion || "",
        licenseName: component.licenseName || "",
        url: formData.url || "",
        copyrightInfo: formData.copyright || "",
        licenseText: formData.licenseText || "",
        componentLicense: {
          componentName: component.componentName || "",
          componentVersion: component.componentVersion || "",
          licenseIdentifier: component.licenseName || "",
          url: formData.url || "",
          copyright: formData.copyright || "",
          licenseText: formData.licenseText || "",
        }
      };

      // Call the API to update the component
      await updateNoticeInfoComponent.mutateAsync(requestBody);

      // Also call the parent onSave to update the local state
      const updatedComponent = {
        ...component,
        ...formData,
      };
      onSave(updatedComponent);
    } catch (error) {
      console.error("Save error:", error);
      // Error handling is done in the mutation's onError
    }
  };

  // Handle position navigation
  const handlePositionChange = (position) => {
    // Handle empty string or invalid input
    if (position === '' || isNaN(position) || position < 1) {
      return;
    }
    
    const targetIndex = Math.max(1, Math.min(position, totalCount));
    const actualIndex = targetIndex - 1; // Convert to 0-based index
      
    if (hasUnsavedChanges && !isSaved) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => onNavigate(actualIndex));
    } else {
      onNavigate(actualIndex);
    }
  };

  // Handle navigation with unsaved changes check
  const handleNavigationClick = (direction) => {
    if (hasUnsavedChanges && !isSaved) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => onNavigate(direction));
    } else {
      onNavigate(direction);
    }
  };

  // Handle close with unsaved changes check
  const handleCloseClick = () => {
    if (hasUnsavedChanges && !isSaved) {
      setShowUnsavedWarning(true);
      setPendingAction(() => onClose);
    } else {
      onClose();
    }
  };

  // Handle unsaved changes warning
  const handleUnsavedWarningConfirm = () => {
    setShowUnsavedWarning(false);
    setHasUnsavedChanges(false);
    
    // Reset form data to original component values
    if (component) {
      setFormData({
        noticeInfoComponentSeq: component.noticeInfoComponentSeq || null,
        noticeInfoSeq: component.noticeInfoSeq || null,
        url: component.url || "",
        copyright: component.copyright || "",
        licenseText: component.licenseText || "",
        urlSelection: component.urlSelection || null,
        copyrightSelection: component.copyrightSelection || null,
        licenseTextSelection: component.licenseTextSelection || null,
      });
    }
    
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  const handleUnsavedWarningCancel = () => {
    setShowUnsavedWarning(false);
    setPendingAction(null);
  };

  useEscapeKey(handleCloseClick, isOpen);

  if (!isOpen || !component) return null;

  const canNavigatePrev = currentIndex > 0;
  const canNavigateNext = currentIndex < totalCount - 1;

  return (
    <>
      <div className={`fixed inset-0 ${colors.bgBlurModal} z-60 flex items-center justify-start md:justify-center backdrop-blur-[1px] overflow-x-auto p-4`} data-testid={`${dataTestId}-overlay`}>
        <div
          className={`${colors.bgModal} max-h-[80vh] w-[90vw] max-w-[1000px] flex-shrink-0 rounded-lg shadow-xl flex flex-col`}
          data-testid={dataTestId}
        >
          {/* Header */}
          <div className={`px-6 py-4 flex-shrink-0`} data-testid={`${dataTestId}-header`}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className={`${colors.textPrimary} text-xl font-bold`} data-testid={`${dataTestId}-title`}>
                  {component.componentName} ({component.componentVersion})
                </h2>
                {component.licenseName && (
                  <p className={`text-sm ${colors.textSecondary} mt-1`}>{component.licenseName}</p>
                )}
              </div>
              
            </div>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6 min-h-0" data-testid={`${dataTestId}-content`}>
            {/* URL Section */}
            <div className="flex items-start space-x-6" data-testid={fieldContainerTestId("url")}>
              <div className="w-32 flex-shrink-0">
                <label className={`block text-sm font-medium ${colors.textPrimary} pt-2`} data-testid={fieldLabelTestId("url")}>{t("common.url")}</label>
              </div>
              <div className="flex-1 space-y-3">
                <CustomTwoColumnSelect
                  options={sortOptionsWithCustomFirst(component.urlOptions || [])}
                  value={formData.urlSelection}
                  onChange={(selection) => handleSelectionChange("urlSelection", selection)}
                  placeholder={t("open-source.select-url-source")}
                  size="md"
                  nameHeader={t("open-source.url-header")}
                  originalHeader={t("open-source.original-header")}
                  nameColumn="displayName"
                  originalColumn="original"
                  data-testid={fieldInputTestId("url-selection")}
                />
                <CustomInput
                  type="url"
                  value={formData.url}
                  onChange={(e) => handleFieldChange("url", e.target.value)}
                  disabled={isFieldDisabled("url")}
                  placeholder={t("common.url-placeholder")}
                  className="w-full"
                  data-testid={fieldInputTestId("url")}
                />
              </div>
            </div>

            {/* Copyright Section */}
            <div className="flex items-start space-x-6" data-testid={fieldContainerTestId("copyright")}>
              <div className="w-32 flex-shrink-0">
                <label className={`block text-sm font-medium ${colors.textPrimary} pt-2`} data-testid={fieldLabelTestId("copyright")}>
                  {t("open-source.copyright")}
                </label>
              </div>
              <div className="flex-1 space-y-3">
                <CustomTwoColumnSelect
                  options={sortOptionsWithCustomFirst(component.copyrightOptions || [])}
                  value={formData.copyrightSelection}
                  onChange={(selection) => handleSelectionChange("copyrightSelection", selection)}
                  placeholder={t("open-source.select-copyright")}
                  size="md"
                  nameHeader={t("open-source.copyright-header")}
                  originalHeader={t("open-source.original-header")}
                  nameColumn="displayName"
                  originalColumn="original"
                  data-testid={fieldInputTestId("copyright-selection")}
                />
                <CustomTextarea
                  value={formData.copyright}
                  onChange={(e) => handleFieldChange("copyright", e.target.value)}
                  rows={7}
                  size="sm"
                  disabled={isFieldDisabled("copyright")}
                  placeholder={t("open-source.copyright-placeholder")}
                  maxLength={CHARACTER_LENGTH.LICENSE_COMMENT}
                  data-testid={fieldInputTestId("copyright")}
                />
                <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid={`${dataTestId}-field-copyright-char-count`}>
                  {(formData.copyright || "").length}/{CHARACTER_LENGTH.LICENSE_COMMENT}
                </p>
              </div>
            </div>

            {/* License Text Section */}
            <div className="flex items-start space-x-6" data-testid={fieldContainerTestId("license-text")}>
              <div className="w-32 flex-shrink-0">
                <label className={`block text-sm font-medium ${colors.textPrimary} pt-2`} data-testid={fieldLabelTestId("license-text")}>
                  {t("open-source.license-text")}
                </label>
              </div>
              <div className="flex-1 space-y-3">
                <CustomTwoColumnSelect
                  options={sortOptionsWithCustomFirst(component.licenseTextOptions || [])}
                  value={formData.licenseTextSelection}
                  onChange={(selection) => handleSelectionChange("licenseTextSelection", selection)}
                  placeholder={t("open-source.select-license-text")}
                  size="md"
                  nameHeader={t("open-source.license-text-header")}
                  originalHeader={t("open-source.original-header")}
                  nameColumn="displayName"
                  originalColumn="original"
                  data-testid={fieldInputTestId("license-text-selection")}
                />
                <CustomTextarea
                  value={formData.licenseText}
                  onChange={(e) => handleFieldChange("licenseText", e.target.value)}
                  rows={10}
                  size="sm"
                  disabled={isFieldDisabled("licenseText")}
                  placeholder={t("open-source.license-text-placeholder")}
                  maxLength={CHARACTER_LENGTH.LICENSE_TEXT}
                  data-testid={fieldInputTestId("license-text")}
                />
                <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid={`${dataTestId}-field-license-text-char-count`}>
                  {(formData.licenseText || "").length}/{CHARACTER_LENGTH.LICENSE_TEXT}
                </p>
              </div>
            </div>
          </div>

          {/* Footer - Sticky */}
          <div className={`px-6 py-4 flex-shrink-0`} data-testid={`${dataTestId}-footer`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Position indicator with input */}
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium ${colors.textSubTitle}`}>NO</span>
                  <input
                    type="number"
                    min="1"
                    max={totalCount}
                    value={targetPosition}
                    data-testid={fieldInputTestId("position")}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setTargetPosition('');
                        return;
                      }
                      const position = parseInt(value);
                      if (!isNaN(position) && position >= 1 && position <= totalCount) {
                        setTargetPosition(position);
                        // Navigate immediately when a valid position is entered
                        handlePositionChange(position);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handlePositionChange(targetPosition);
                      }
                    }}
                    onBlur={() => handlePositionChange(targetPosition)}
                    className={`h-8 w-12 rounded border px-2 py-1 text-sm font-medium text-center focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.borderHoverPrimary}`}
                  />
                  <span className={`text-sm font-medium ${colors.textSubTitle}`}>
                    {t("common.table.of")} {totalCount} {t("dashboard.components")}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Navigation buttons */}
                <button
                  type="button"
                  onClick={() => handleNavigationClick("prev")}
                  disabled={!canNavigatePrev}
                  className={`flex items-center font-semibold text-sm gap-1 p-2 ${colors.textActive} transition-colors disabled:cursor-not-allowed disabled:opacity-30`}
                  title="Previous page"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                  <span className={`${colors.textActive} font-semibold text-sm`}>{t("common.table.prev")}</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleNavigationClick("next")}
                  disabled={!canNavigateNext}
                  className={`flex items-center gap-1 p-2 font-semibold text-sm ${colors.textActive} transition-colors disabled:cursor-not-allowed disabled:opacity-50`}
                  title="Next page"
                >
                  <span className={`${colors.textActive} font-semibold text-sm`}>{t("common.table.next")}</span>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              <div className="flex items-center space-x-3">
                <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={handleCloseClick}>
                  {t("common.cancel-button")}
                </OutlineButton>
                <PrimaryButton
                  data-testid={`${dataTestId}-confirm-button`}
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges || isSaving || isSaved}
                >
                  {isSaving ? (
                    <div className="flex items-center">
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                      {t("common.saving")}
                    </div>
                  ) : isSaved ? (
                    <div className="flex items-center">
                      <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {t("common.save-button")}
                    </div>
                  ) : (
                    t("common.save-button")
                  )}
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Unsaved Changes Warning Modal */}
      <UnsavedChangesModal
        isOpen={showUnsavedWarning}
        onClose={handleUnsavedWarningCancel}
        onConfirm={handleUnsavedWarningConfirm}
        title={t("common.unsaved-changes")}
        message={t("common.unsaved-changes-message")}
        confirmText={t("common.ok-button")}
        cancelText={t("common.cancel-button")}
        confirmButtonVariant="primary"
      />
    </>
  );
};

export default NoticeInformationEditModal;
